import type { GeoPt } from './types'
import { mercY, mercYToLat } from './geo'

export interface MapView {
  center: GeoPt
  zoom: number
}

/**
 * The opening view for a map with nothing on it.
 *
 * A map has to start somewhere, and the previous answer — the sample data's
 * centre, at street-level zoom — pointed at one stretch of Swedish forest.
 * At that zoom the map reads as a claim: *this is where your ground is*. For a
 * team anywhere else, and for the German and Finnish markets in particular,
 * that claim is simply wrong.
 *
 * There is no cheap way to do better. The teams list carries no map data
 * (`groupToTeam` builds every entry with an empty map), so centring on the
 * hunter's other grounds would mean one hunting-grounds request per team just
 * to place an empty map. Asking the browser for the user's position raises a
 * permission prompt on a page where the map is incidental, and has to fall
 * back to something anyway when it is denied.
 *
 * So this asserts nothing instead: a regional overview wide enough to hold
 * every market the app is localized for (sv, no, fi, de), zoomed out far
 * enough to read as "nowhere in particular yet" rather than as a location.
 * The map pans and zooms, and the empty state tells the reader to open the map
 * and draw a border.
 *
 * Zoom 5 is deliberate: the tile source stops serving below 4, so this keeps a
 * step of headroom while still framing northern and central Europe.
 */
export const EMPTY_MAP_VIEW: MapView = {
  center: { lat: 58.0, lon: 15.0 },
  zoom: 5,
}

/**
 * Pick a centre and zoom that fit `points` inside a `cssW`×`cssH` viewport.
 *
 * The tile canvas and the SVG overlay share the same px-per-world scale, so
 * fitting the canvas fits the overlay too.
 *
 * The centre is the midpoint of the *projected* bounds, not of the latitudes:
 * Mercator stretches towards the poles, so the average of two latitudes is not
 * the latitude halfway down the screen between them, and using it leaves one
 * edge closer to the frame than the other.
 *
 * A set of points with no extent — one point, or several at the same place —
 * fixes where to look but not how far out, so the zoom comes from
 * `noExtentZoom` rather than from a span of zero.
 */
export function fitViewToPoints(
  points: readonly GeoPt[],
  cssW: number,
  cssH: number,
  {
    minZ = 4,
    maxZ = 16,
    padding = 0.7,
    noExtentZoom = maxZ,
  }: { minZ?: number; maxZ?: number; padding?: number; noExtentZoom?: number } = {},
): { center: GeoPt; zoom: number } | null {
  if (points.length === 0) return null
  let minLon = Infinity, maxLon = -Infinity, minY = Infinity, maxY = -Infinity
  for (const p of points) {
    const y = mercY(p.lat)
    minLon = Math.min(minLon, p.lon); maxLon = Math.max(maxLon, p.lon)
    minY = Math.min(minY, y); maxY = Math.max(maxY, y)
  }
  const center: GeoPt = { lat: mercYToLat((minY + maxY) / 2), lon: (minLon + maxLon) / 2 }
  const fracX = (maxLon - minLon) / 360
  const fracY = maxY - minY
  if (fracX <= 1e-9 && fracY <= 1e-9) return { center, zoom: noExtentZoom }
  let z = maxZ
  if (fracX > 1e-9) z = Math.min(z, Math.log2((cssW * padding) / (256 * fracX)))
  if (fracY > 1e-9) z = Math.min(z, Math.log2((cssH * padding) / (256 * fracY)))
  if (!Number.isFinite(z)) z = noExtentZoom
  return { center, zoom: Math.max(minZ, Math.min(maxZ, Math.floor(z))) }
}

/** No boundary → no points → no fit. */
const NO_POINTS: readonly GeoPt[] = []

/**
 * The points a hunting-ground map's opening view fits to: the **boundary, and
 * only the boundary**.
 *
 * Pins are content drawn on the ground, not a definition of where it is — in
 * production a team map doubles as a personal POI collection, so a pin can sit
 * hundreds of kilometres away (parking in another town, a hotel). Fitting to
 * the boundary *and* the pins opened a 25 m x 48 m property on a view covering
 * half of Sweden (the CLUB-559 / #1237 regression). Per the product rule: zoom
 * in towards the borders only; when there is no border, do not zoom in at all —
 * return no points so the caller opens on its regional overview rather than
 * framing (and zooming out to) scattered pins.
 *
 * A boundary needs three points to bound anything — two are a line the drawing
 * flow never completes.
 *
 * Deliberately NOT what the plan-hunt stand pickers use: there the stands ARE
 * the subject of the view, so `StandsMap` frames the stands (falling back to the
 * boundary when a hunt has none) rather than going through this.
 */
export function groundViewPoints(boundary: readonly GeoPt[]): readonly GeoPt[] {
  return boundary.length >= 3 ? boundary : NO_POINTS
}

/** The area shape {@link groundFramePoints} needs — a ring list and an optional
 *  `[SW, NE]` bounding box. Structural so any `GeoArea` satisfies it without this
 *  module depending on the full type. */
export interface FramableArea {
  rings: readonly (readonly GeoPt[])[]
  bounds?: readonly [GeoPt, GeoPt]
}

/**
 * The points a ground map's opening view frames on: the border rings when the
 * areas carry them, and otherwise the `[SW, NE]` bounding-box corners of any area
 * that has one.
 *
 * A ground whose polygon is offloaded to storage arrives with empty rings and only
 * a bbox; without this it framed on nothing and the map fell to its regional
 * default, which a member of many teams read as "the map jumped somewhere random".
 * The bbox corners are returned raw (not through {@link groundViewPoints}, whose
 * ≥3-point guard exists to reject an incomplete *border* — a bbox is a complete two
 * points), so the caller's `fitViewToPoints` frames them directly.
 *
 * Ring points win when present, so a ground that has a real outline is unaffected;
 * an empty result means neither geometry exists and the caller opens on its
 * regional overview.
 */
export function groundFramePoints(areas: readonly FramableArea[]): readonly GeoPt[] {
  const ringPoints = groundViewPoints(areas.flatMap((a) => a.rings.flat()))
  if (ringPoints.length > 0) return ringPoints
  return areas.flatMap((a) => a.bounds ?? [])
}

/**
 * The `[minLon, minLat, maxLon, maxLat]` extent of `points`, or null when empty —
 * the tuple order MapLibre's `fitBounds` (and our `MapView`'s `initialBounds`)
 * expects. Shared so the several map surfaces that frame on a bag of points do not
 * each hand-roll the same min/max loop and drift.
 */
export function pointsToBBox(points: readonly GeoPt[]): [number, number, number, number] | null {
  if (points.length === 0) return null
  let minLon = Infinity
  let minLat = Infinity
  let maxLon = -Infinity
  let maxLat = -Infinity
  for (const p of points) {
    if (p.lon < minLon) minLon = p.lon
    if (p.lon > maxLon) maxLon = p.lon
    if (p.lat < minLat) minLat = p.lat
    if (p.lat > maxLat) maxLat = p.lat
  }
  return [minLon, minLat, maxLon, maxLat]
}
